import { Link } from 'react-router-dom';

import { Modal } from '../albums/Modal';
import { BroadcastOrigin } from './Simulcast';
import { elapsed, type LiveRoom, type Submissions } from './api';
import { useLiveHosts } from './useLiveHosts';

/**
 * How a room's door reads, in three words.
 *
 * The tone carries as much as the label: green for open is the same green the
 * queue uses for a host pick, and grey for closed is the site's "nothing to do
 * here" rather than an error - a closed queue is a host running their own set,
 * not a fault.
 */
const DOOR: Record<Submissions, { label: string; tone: string }> = {
  open: { label: 'taking submissions', tone: 'text-fresh' },
  followers: { label: 'followers only', tone: 'text-accent' },
  closed: { label: 'submissions closed', tone: 'text-faint' },
};

/**
 * Everyone who is on air (§6.8).
 *
 * ## Why this is a modal and not a page
 *
 * A broadcast is at most a couple of hours long and there are rarely more than
 * a handful running. Sending somebody to a route for that loses the page they
 * were on, and they came to this list to *leave* for a room anyway - so the
 * cheapest thing that answers "who is on right now" wins.
 *
 * There is a page for browsing rooms eventually; it is a different feature with
 * genres and a history, and it is not this.
 */
export function OnAirList({ onClose }: { onClose: () => void }) {
  const { items, isPending } = useLiveHosts();

  return (
    <Modal title={items.length === 1 ? '1 host on air' : `${items.length} hosts on air`} onClose={onClose}>
      {isPending ? (
        <p className="py-6 text-center text-sm text-soft">Looking…</p>
      ) : items.length === 0 ? (
        // Reachable: the count that opened this can go stale between the poll
        // and the click, and the last host closing is exactly when it does.
        <p className="py-6 text-center text-sm text-soft">Nobody is on air right now.</p>
      ) : (
        <ul className="flex flex-col gap-2" data-on-air-list>
          {items.map((room) => (
            <li key={room.id}>
              <RoomRow room={room} onClose={onClose} />
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

function RoomRow({ room, onClose }: { room: LiveRoom; onClose: () => void }) {
  const door = DOOR[room.submissions];

  /*
   * The row is a container, not one big anchor.
   *
   * It used to be a `<Link>` wrapping everything, which cannot hold the
   * "watch on Twitch" link - an anchor inside an anchor is invalid and browsers
   * un-nest it, leaving a stray link and a dead patch in the row. So the row
   * links via a stretched overlay and the simulcast sits above it.
   */
  return (
    <div
      data-on-air={room.username}
      className="relative flex items-center gap-3.5 rounded-[var(--radius-card)] border border-line p-3 transition-colors hover:border-line-strong hover:bg-raised"
    >
      {/* Covers the row, sits under anything that needs its own click. */}
      <Link
        to={`/${room.username}`}
        onClick={onClose}
        aria-label={`${room.host.display_name}'s broadcast`}
        className="absolute inset-0 rounded-[var(--radius-card)]"
      />
      {room.now_playing?.artwork_small ? (
        <img
          src={room.now_playing.artwork_small}
          alt=""
          className="h-12 w-12 shrink-0 rounded-[var(--radius-control)] object-cover"
        />
      ) : (
        <div
          aria-hidden
          className="h-12 w-12 shrink-0 rounded-[var(--radius-control)]"
          style={{ backgroundImage: 'repeating-linear-gradient(45deg,var(--color-blank) 0 6px,var(--color-blank-alt) 6px 12px)' }}
        />
      )}

      <div className="pointer-events-none relative flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex min-w-0 items-baseline gap-2">
          <span className="truncate text-sm font-bold text-ink">{room.host.display_name}</span>
          {/* The set's name, if it has one. Most broadcasts are just somebody
              going live, and an invented title would be worse than none. */}
          {room.name && <span className="truncate font-mono text-[10px] text-ghost">{room.name}</span>}
        </div>

        <span className="truncate text-[13px] text-soft">
          {room.now_playing
            ? `${room.now_playing.title} — ${room.now_playing.artist}`
            : 'Between tracks'}
        </span>

        <span className="flex flex-wrap items-center gap-x-2.5 gap-y-1 font-mono text-[10px] text-faint">
          <span className={door.tone}>{door.label}</span>
          <span aria-hidden>·</span>
          <span>{room.queue_count === 1 ? '1 in queue' : `${room.queue_count} in queue`}</span>
          <span aria-hidden>·</span>
          <span>
            {room.listener_count === 1 ? '1 listening' : `${room.listener_count} listening`}
          </span>
          {room.live_started_at && (
            <>
              <span aria-hidden>·</span>
              <span>on air {elapsed(room.live_started_at)}</span>
            </>
          )}
          {room.genre && (
            <>
              <span aria-hidden>·</span>
              <span>{room.genre}</span>
            </>
          )}
          <span aria-hidden>·</span>
          {/* Always present, so the line says where every broadcast is and not
              only the ones that left. A named platform re-enables pointer
              events above the row overlay, because it is the one thing in the
              row that goes somewhere else; the mark for this platform is not a
              link and stays under it. */}
          <BroadcastOrigin simulcast={room.simulcast} size="sm" interactive />
        </span>
      </div>

      <span aria-hidden className="shrink-0 text-faint">
        ›
      </span>
    </div>
  );
}
